import React, { FC } from 'react'
import { system as s } from '@moonpig/launchpad-system'
import { breakpoint, styled } from '@moonpig/launchpad-utils'
import { Text, Flex } from '@moonpig/launchpad-components'
import { IconSystemCross, IconSystemClock } from '@moonpig/launchpad-assets'
import { useCoreLocaleText } from '@moonpig/web-core-locale-text'
import { RecentRow } from '../types'
import { useLocaleText } from '../../locale'
import {
  CLEAR_BUTTON_SIZE_DESKTOP_PX,
  CLEAR_BUTTON_SIZE_MOBILE_PX,
  LIST_ICON_SIZE_PX,
} from '../constants'

const StyledRecentSearchText = styled(Text)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
`

const StyledRecentSearchSecondaryText = styled(Text)`
  white-space: nowrap;
  width: 20%;
  ${s({ color: 'colorBlack60' })}
`

const StyledClearButton = styled.button`
  width: ${CLEAR_BUTTON_SIZE_MOBILE_PX};
  height: ${CLEAR_BUTTON_SIZE_MOBILE_PX};
  align-items: center;
  justify-content: center;

  ${s({ color: 'colorInteractionControl', borderRadius: 1 })}

  ${breakpoint('md')} {
    width: ${CLEAR_BUTTON_SIZE_DESKTOP_PX};
    height: ${CLEAR_BUTTON_SIZE_DESKTOP_PX};
  }

  &:hover,
  &:focus {
    ${s({ bgcolor: 'colorBackground01', color: 'colorInteractionIconHover' })}
  }
`

const StyledIconWrapper = styled(Flex)`
  ${s({
    bgcolor: 'colorBackground01',
    borderRadius: 1,
    p: 4,
    mr: 4,
    color: 'colorInteractionControl',
  })}
`

type RecentSearchProps = {
  row: RecentRow
  onRemoveItem: (item: {
    department: string
    searchTerm: string
    value: string
  }) => void
}

export const RecentSearch: FC<RecentSearchProps> = ({ row, onRemoveItem }) => {
  const item = row.item as {
    department: string
    searchTerm: string
    value: string
  }
  const t = useLocaleText()
  const coreLocale = useCoreLocaleText()
  const capitalizedSearchTerm = `${item.searchTerm[0].toUpperCase()}${item.searchTerm?.slice(
    1,
  )}`

  const getDepartmentText = (dept: string) => {
    if (dept === 'all_cards') {
      return coreLocale('common.cards')
    }
    if (dept === 'all_flowers_and_plants') {
      return coreLocale('common.flowers')
    }
    if (dept === 'all_gifts') {
      return coreLocale('common.gifts')
    }
    /* istanbul ignore next */
    return dept
  }

  return (
    <Flex justifyContent="space-between" alignItems="center">
      <Flex alignItems="center">
        <StyledIconWrapper>
          <IconSystemClock
            width={LIST_ICON_SIZE_PX}
            height={LIST_ICON_SIZE_PX}
          />
        </StyledIconWrapper>
        <StyledRecentSearchText>
          {capitalizedSearchTerm}
          <StyledRecentSearchSecondaryText>
            &nbsp;{getDepartmentText(item.department)}
          </StyledRecentSearchSecondaryText>
        </StyledRecentSearchText>
      </Flex>
      <StyledClearButton
        aria-label={`${t(
          'search.clear_recent_search',
        )} ${capitalizedSearchTerm}`}
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            e.stopPropagation()
            onRemoveItem(item)
          }
        }}
        onClick={e => {
          e.preventDefault()
          e.stopPropagation()
          onRemoveItem(item)
        }}
        data-testid="lp-nav-search-recent-remove-item"
      >
        <IconSystemCross
          width={LIST_ICON_SIZE_PX}
          height={LIST_ICON_SIZE_PX}
          aria-hidden
        />
      </StyledClearButton>
    </Flex>
  )
}
